<script setup lang="ts">
import type { HTMLAttributes, InputTypeHTMLAttribute } from 'vue';
import type { ControlSize, ControlWidth } from '@/components/base/_internal/control';
import { useVModel } from '@vueuse/core';
import { X } from 'lucide';
import { computed } from 'vue';
import { controlWidthClass, useControlSize, useControlWidth, useFieldControl } from '@/components/base/_internal/control';
import { SphIcon } from '@/components/base/icon/';
import { useI18n } from '@/composables/i18n';
import { currencySymbol, currentIntlLocale, parseLocalizedNumber, toIntlLocale } from '@/utils/number';
import { cn } from '@/utils/shadcn/lib/utils';
import InputGroup from './internal/InputGroup.vue';
import InputGroupAddon from './internal/InputGroupAddon.vue';
import InputGroupInput from './internal/InputGroupInput.vue';
import NumberInput from './internal/NumberInput.vue';
import { inputVariants } from './variants';

defineOptions({
    compatConfig: {
        MODE: 3,
    },
    inheritAttrs: false,
});

const props = withDefaults(defineProps<{
    defaultValue?: string | number | null
    modelValue?: string | number | null
    /** `money` is a preset over `number`: two decimals plus a currency addon. */
    type?: InputTypeHTMLAttribute | 'money'
    /** Fixed number of decimals, e.g. `2`. Ignored outside `number` and `money`. */
    decimals?: number
    /** Escape hatch overriding both the defaults and `decimals`. */
    formatOptions?: Intl.NumberFormatOptions
    /** ISO code picking the addon symbol for `type="money"`. */
    currency?: string
    /** Defaults to the locale the page was rendered in. */
    locale?: string
    /** Falls back to the size a container provides (see `SphControlProvider`), then to `md`. */
    size?: ControlSize
    /** Falls back to the width a container provides, then fills the container. */
    width?: ControlWidth
    /** Shows a button in the suffix addon that empties the field. */
    clearable?: boolean
    class?: HTMLAttributes['class']
    inputClass?: HTMLAttributes['class']
    inputStyle?: HTMLAttributes['style']
    required?: boolean
    invalid?: boolean
    disabled?: boolean
}>(), {
    currency: 'EUR',
    size: undefined,
    width: undefined,
    clearable: false,
});

const emits = defineEmits<{
    'update:modelValue': [payload: string | number | null]
}>();

const modelValue = useVModel(props, 'modelValue', emits, {
    passive: true,
    defaultValue: props.defaultValue,
});

const { i18n } = useI18n();

const size = useControlSize(() => props.size, 'md');
const width = useControlWidth(() => props.width, 'text');
const field = useFieldControl(() => props, () => width.value);

const isNumeric = computed(() => props.type === 'number' || props.type === 'money');
const isMoney = computed(() => props.type === 'money');

const locale = computed(() => (props.locale ? toIntlLocale(props.locale) : currentIntlLocale()));

const formatOptions = computed<Intl.NumberFormatOptions>(() => {
    const decimals = props.decimals ?? (isMoney.value ? 2 : undefined);

    return {
        ...(decimals === undefined ? {} : { minimumFractionDigits: decimals, maximumFractionDigits: decimals }),
        ...props.formatOptions,
    };
});

const numericValue = computed<number | null>(() => {
    const current = modelValue.value;

    if (typeof current === 'number') {
        return Number.isFinite(current) ? current : null;
    }

    return typeof current === 'string' ? parseLocalizedNumber(current) : null;
});

const isEmpty = computed(() => modelValue.value === null || modelValue.value === undefined || modelValue.value === '');
const showClearButton = computed(() => !isEmpty.value && !field.value.disabled);

function clear(event: Event) {
    modelValue.value = isNumeric.value ? null : '';

    const group = (event.currentTarget as HTMLElement).closest('[data-slot="input-group"]');
    group?.querySelector<HTMLInputElement>('input:not([type="hidden"])')?.focus();
}
</script>

<template>
    <InputGroup
        :class="cn(inputVariants({ size }), controlWidthClass(width), props.class)"
        :data-disabled="field.disabled"
    >
        <template
            v-if="$slots.prefix"
        >
            <InputGroupAddon>
                <slot name="prefix"></slot>
            </InputGroupAddon>
        </template>
        <NumberInput
            v-if="isNumeric"
            v-bind="$attrs"
            :model-value="numericValue"
            :locale="locale"
            :format-options="formatOptions"
            :class="props.inputClass"
            :style="props.inputStyle"
            :id="$attrs.id || field.id"
            :aria-describedby="$attrs['aria-describedby'] || field.describedBy"
            :required="field.required"
            :invalid="field.invalid"
            :disabled="field.disabled"
            @update:model-value="modelValue = $event"
        />
        <InputGroupInput
            v-else
            v-bind="$attrs"
            v-model="modelValue"
            :type="props.type"
            :class="props.inputClass"
            :style="props.inputStyle"
            :id="$attrs.id || field.id"
            :aria-describedby="$attrs['aria-describedby'] || field.describedBy"
            :required="field.required"
            :invalid="field.invalid"
            :disabled="field.disabled"
        />
        <InputGroupAddon
            v-if="props.clearable || $slots.suffix || isMoney"
            align="inline-end"
        >
            <div
                v-if="props.clearable"
                :aria-label="i18n.t('Eingabe löschen')"
                :aria-hidden="!showClearButton"
                :tabindex="showClearButton ? 0 : -1"
                role="button"
                class="clear-button"
                :class="{ show: showClearButton }"
                @click.prevent.stop="clear"
                @keydown.enter.prevent.stop="clear"
                @keydown.space.prevent.stop="clear"
            >
                <SphIcon
                    :icon="X"
                    size="sm"
                />
            </div>
            <slot name="suffix">
                <template v-if="isMoney">
                    {{ currencySymbol(props.currency, locale) }}
                </template>
            </slot>
        </InputGroupAddon>
    </InputGroup>
</template>

<style scoped>
@reference '@/assets/styles/config.css';

.clear-button {
    @apply w-5 h-full flex items-center justify-center rounded-sm cursor-pointer text-muted-foreground hover:text-accent transition-all border border-transparent focus-visible:border-ring/50 outline-none select-none pointer-events-none opacity-0;

    &.show {
        @apply opacity-100 pointer-events-auto;
    }
}
</style>
